import { useTranslation } from 'react-i18next';
import FileUpload from './fileUpload';
import { Form, FormItem, Paragraph, Spin, Switch } from '../../Ant';
import TextAreaField from '../../textAreaField/textAreaField';
import { AddSubjectWrapper } from '../../../assets/styles/components/dashboard.styled';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../addTransaction/addEditTransactionHeader';
import { usePage } from '../../../context/PageContext';
import { useLocation, useParams } from 'react-router-dom';
import { useCreateNote } from '../../../hooks/useCreateNote';
import { FileInfo, INote } from '../../../interface/notes';
import {
  entityFilterMap,
  NOTE_QUERY_KEYS_TO_INVALIDATE,
  NotesEntity,
  SignificanceTypeValue,
} from '../../../constants/notes';
import { useUpdateNote } from '../../../hooks/useUpdateNote';
import { useCreateNoteAttachment } from '../../../hooks/useCreateNoteAttachment';
import { useFetchNoteDetail } from '../../../hooks/useFetchNoteDetail';
import { useDeleteNoteAttachment } from '../../../hooks/useDeleteNoteAttachment';
import { useDeleteNote } from '../../../hooks/useDeleteNote';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { TransactionViewTab } from '../../../constants/transaction';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { AnalyticsPage } from '../../../interface/analytics';
import CustomModal from '../../informationPopup/customModal';
import { Slash } from '../../icons';
import PinnedSaveButton from '../../pinnedSaveButton/pinnedSaveButton';
import { useVersionConflict } from '../../../hooks/useVersionConflict';
import { fetchRecordVersion } from '../../../helpers/versionConflictHelper';
import { API_URLS } from '../../../constants/apiUrl';

const EditNote = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { goBack } = useNavigationStack();
  const { noteId } = useParams();
  const entity = location?.state?.entity;
  const entityId = location?.state?.entityId;
  const { data: notesDetail, isLoading } = useFetchNoteDetail({
    id: noteId,
  });
  const maxChars = 1000;
  const [isImportant, setIsImportant] = useState<boolean>(false);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [note, setNote] = useState<string>('');
  const [initialNote, setInitialNote] = useState<INote>();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const createNoteMutation = useCreateNote();
  const editNoteMutation = useUpdateNote();
  const { latestVersion, handleVersionConflict } = useVersionConflict({
    fetchLatestVersion: async () =>
      noteId ? fetchRecordVersion(`${API_URLS.NOTES_LIST}/${noteId}`) : undefined,
    // The note detail is not invalidated: the form is refilled whenever it changes, which would discard what the user typed
    queriesToInvalidate: NOTE_QUERY_KEYS_TO_INVALIDATE,
  });
  const createNoteAttachmentMutation = useCreateNoteAttachment();
  const deleteNoteAttachmentMutation = useDeleteNoteAttachment();
  const deleteNote = useDeleteNote();

  useEffect(() => {
    trackPage(AnalyticsPage.TIMER_ADD_EDIT);
  }, [trackPage]);

  const handleBack = () => {
    setNote('');
    setIsImportant(false);
    goBack({ fromTab: TransactionViewTab.notes });
  };
  useEffect(() => {
    if (notesDetail) {
      setInitialNote(notesDetail);
      setNote(notesDetail.note || '');
      setIsImportant(notesDetail.significance === SignificanceTypeValue.important);
      if (notesDetail?.attachmentFileData) {
        setFileInfo({
          name: notesDetail.attachmentFileName,
          size: notesDetail.attachmentFileSize,
          file: notesDetail.attachmentFileData as unknown as File,
        });
      } else {
        setFileInfo(null);
      }
    }
  }, [notesDetail]);
  const handleConfirmDelete = () => {
    deleteNote.mutate(noteId ?? '', {
      onSettled: () => {
        handleBack();
      },
    });
  };
  const handleCreateTransactionNoteAttachment = (noteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    createNoteAttachmentMutation.mutate(
      {
        notesId: noteId,
        data: formData,
      },
      {
        onSuccess: () => {
          handleBack();
        },
      },
    );
  };
  const saveAttachmentChanges = () => {
    if (notesDetail?.attachmentFileData && fileInfo === null) {
      deleteNoteAttachmentMutation.mutate(notesDetail?.id);
    }
    if (fileInfo && fileInfo.file !== (notesDetail?.attachmentFileData as unknown as File)) {
      handleCreateTransactionNoteAttachment(noteId ?? '', fileInfo.file);
    } else {
      handleBack();
    }
  };
  const handleEditNote = () => {
    const significance = isImportant
      ? SignificanceTypeValue.important
      : SignificanceTypeValue.normal;
    const hasNoteChanged = note !== (notesDetail?.note || '');
    const hasSignificanceChanged = significance !== notesDetail?.significance;
    if (!hasNoteChanged && !hasSignificanceChanged) {
      saveAttachmentChanges();
      return;
    }

    editNoteMutation.mutate(
      {
        noteId: noteId ?? '',
        request: {
          version: latestVersion ?? notesDetail?.version,
          ...(hasNoteChanged ? { note } : {}),
          ...(hasSignificanceChanged ? { significance } : {}),
        } as unknown as INote,
        params: {},
      },
      {
        onSuccess: saveAttachmentChanges,
        onError: handleVersionConflict,
      },
    );
  };
  const handleAddNote = () => {
    const fieldName = entityFilterMap[entity as NotesEntity];
    const noteData: Record<string, string> = {
      note,
      significance: isImportant ? SignificanceTypeValue.important : SignificanceTypeValue.normal,
    };

    if (fieldName) {
      noteData[fieldName] = entityId;
    }
    createNoteMutation.mutate(noteData, {
      onSuccess: (record: INote) => {
        if (fileInfo && fileInfo.file !== (notesDetail?.attachmentFileData as unknown as File)) {
          handleCreateTransactionNoteAttachment(record?.id, fileInfo.file);
        } else {
          handleBack();
        }
      },
    });
  };
  const handleNoteSave = () => {
    if (noteId) {
      handleEditNote();
    } else {
      handleAddNote();
    }
  };
  const onBack = () => {
    validateOnFormChanged(() => {
      handleBack();
    });
  };
  const validateOnFormChanged = (onProceed: () => void) => {
    if (noteId) {
      // Edit Note
      const hasNoteChanged = note !== initialNote?.note;
      const hasSignificanceChanged =
        isImportant !== (initialNote?.significance === SignificanceTypeValue.important);
      const hasAttachmentChanged = fileInfo?.name !== initialNote?.attachmentFileName;

      const isFormChanged = hasNoteChanged || hasSignificanceChanged || hasAttachmentChanged;
      setShowDiscardDialog(isFormChanged ?? false);
      setOnDiscardConfirm(() => onProceed);
      if (!isFormChanged) {
        onProceed?.();
      }
    } else {
      // Add Note
      const hasNoteChanged = note !== '';
      const hasSignificanceChanged = isImportant;
      const hasAttachmentChanged = fileInfo?.file !== undefined;
      const isFormChanged = hasNoteChanged || hasSignificanceChanged || hasAttachmentChanged;
      setShowDiscardDialog(isFormChanged ?? false);
      setOnDiscardConfirm(() => onProceed);
      if (!isFormChanged) {
        onProceed?.();
      }
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={onBack}
        showCross
        validateOnFormChanged={validateOnFormChanged}
        title={noteId ? t('note:editNote') : t('note:addNote')}
        handleDelete={noteId ? handleConfirmDelete : undefined}
      />,
    );
  }, [notesDetail, noteId, fileInfo, note, isImportant]);
  return (
    <AddSubjectWrapper vertical gap={15}>
      {isLoading && <Spin fullscreen />}
      <div className="d-flex justify-between items-center imp-note-wrapper">
        <Paragraph
          data-testid="mark-as-important-paragraph"
          $fontsize="12px"
          $color="title"
          $lineheight="100%"
        >
          {t('note:markAsImportant')}
        </Paragraph>
        <Switch
          data-testid="important-button-switch"
          aria-label={t('note:markAsImportant')}
          value={isImportant}
          onChange={(e) => {
            setIsImportant(e);
          }}
        />
      </div>
      <div className="w-full relative">
        <Form layout="vertical">
          <FormItem label={t('note:note')} required={true}>
            <TextAreaField
              data-testid="note-textarea"
              value={note}
              placeholder={t('note:noteTextPlaceholder')}
              minRows={8}
              maxAutoRows={21}
              maxLength={maxChars}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </FormItem>
        </Form>
      </div>
      <FileUpload
        data-testid="file-upload"
        onFile={(file: FileInfo | null) => {
          setFileInfo(file);
        }}
        fileInfo={fileInfo}
      />
      <PinnedSaveButton data-testid="save-button" disabled={!note} onClick={handleNoteSave}>
        {noteId ? t('common:saveChanges') : t('note:addNote')}
      </PinnedSaveButton>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </AddSubjectWrapper>
  );
};

export default EditNote;
